import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { getItemUniqueKey, getPackageId } from '../accommodation/cart'
import { getOptInBundleInclusionsFromRate } from '../accommodation/bundles'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import { addHours, dateIsAfter, dateIsBefore, isBookingWithinDateRange } from 'lib/datetime/dateUtils'
import { min, sum } from 'lib/array/arrayUtils'
import { floatify } from 'lib/maths/mathUtils'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import { isPostPurchaseAncillaryCartMode } from 'checkout/selectors/payment/isPostPurchaseAncillaryPayment'
import { getPropertyRateUpgradeSourceItemTotals, isPropertyRateLoyaltyPackageUpgradeItem } from 'checkout/lib/utils/loyalty/propertyRateLoyaltyUpgrade'
import { applyChannelMarkupAndRoundUpToTen, type ChannelMarkupDetails } from 'lib/channelMarkup/channelMarkup'
import { resolveChannelMarkupDetails, resolveMemberChannelMarkupDetails } from 'lib/channelMarkup/channelMarkupV2'
import type { ChannelMarkupSource } from 'lib/channelMarkup/channelMarkupV2'
import type { IntlShape } from 'react-intl'

/**
 * The bedbank enquiry session carries svc-bedbank's BASE prices (svc-bedbank is unaware of
 * channel markup; svc-order re-applies it at order creation). When a channel markup applies,
 * uplift the session sell prices the same way bedbankRateMap does so the checkout shows the
 * marked price the customer will be charged. The tax-inclusive sell price is marked up;
 * property fees / surcharges are not.
 */
function applyChannelMarkupToSessionPricing(
  session: App.BedbankEnquiry,
  markupDetails: ChannelMarkupDetails,
  memberMarkupDetails: ChannelMarkupDetails,
): App.BedbankEnquiry {
  if (!markupDetails.isApplied && !memberMarkupDetails.isApplied) {
    return session
  }
  const markup = (value: number) => applyChannelMarkupAndRoundUpToTen(value, markupDetails)
  const memberMarkup = (value: number) => applyChannelMarkupAndRoundUpToTen(value, memberMarkupDetails)
  return {
    ...session,
    occupancyPricing: session.occupancyPricing?.map(op => {
      const opInclusiveBase = op.exclusive + op.taxesAndFees
      const inclusive = markup(opInclusiveBase)
      const markedMemberInclusive = op.memberInclusive ? memberMarkup(op.memberInclusive) : undefined
      const otherFees = sum(op.fees ?? [], fee => fee.amount)
      return {
        ...op,
        exclusive: markup(op.exclusive),
        inclusive,
        memberExclusive: op.memberExclusive ? memberMarkup(op.memberExclusive) : op.memberExclusive,
        memberInclusive: markedMemberInclusive ?? op.memberInclusive,
        total: inclusive + otherFees,
        memberTotal: markedMemberInclusive != null ? markedMemberInclusive + otherFees : op.memberTotal,
      }
    }),
    totals: session.totals && {
      ...session.totals,
      exclusive: markup(session.totals.exclusive),
      inclusive: markup(session.totals.exclusive + session.totals.taxesAndFees),
      memberExclusive: session.totals.memberExclusive ? memberMarkup(session.totals.memberExclusive) : session.totals.memberExclusive,
      memberInclusive: session.totals.memberInclusive ? memberMarkup(session.totals.memberInclusive) : session.totals.memberInclusive,
      total: markup(session.totals.exclusive + session.totals.taxesAndFees) + (session.totals.propertyFees ?? 0),
      memberTotal: session.totals.memberInclusive ? memberMarkup(session.totals.memberInclusive) + (session.totals.propertyFees ?? 0) : session.totals.memberTotal,
    },
  }
}

// NB: You can use the returned `RawPropertyRate` for inclusions and bundle information, but you
// mustn't use it for pricing, as the raw rates stored in state have not been enriched with flights.
export function findUnifiedBedbankRateForCheckoutItem(
  item: App.Checkout.BedbankHotelItem,
  offer: App.BedbankOffer,
  propertyRatesByKey: App.OfferState['propertyRates'] | undefined,
): App.RawBedbankPropertyRate | undefined {
  if (!propertyRatesByKey) {
    return undefined
  }

  const allRates = Object.values(propertyRatesByKey)
    .flatMap(state =>
      (state?.rooms ?? [])
        .filter((entry): entry is App.RawAvailablePropertyRoomRates => 'rates' in entry)
        .flatMap(entry => Object.values(entry.rates)),
    )
    .filter((rate): rate is App.RawBedbankPropertyRate => rate.service.type === 'bedbank')

  const ref = item.propertyRateRef
  if (ref?.serviceType === 'bedbank') {
    const refPropertyRates = allRates.filter(rate =>
      rate.propertyId === ref.propertyId &&
      rate.roomId === ref.roomId,
    )
    if (refPropertyRates.length) {
      const scopedRates = ref.bundleId
        ? refPropertyRates.filter(rate => rate.bundle?.bundleId === ref.bundleId)
        : refPropertyRates

      if (ref.groupKey) {
        const byGroupKey = scopedRates.find(rate => rate.groupKey === ref.groupKey)
        if (byGroupKey) return byGroupKey
      }

      const byRateId = scopedRates.find(rate => rate.service.rateId === ref.serviceRateId || rate.rateId === ref.serviceRateId)
      if (byRateId) return byRateId
    }
  }

  const propertyRates = allRates.filter(rate => rate.propertyId === offer.property.id && rate.roomId === item.roomId)
  if (!propertyRates.length) {
    return undefined
  }

  if (item.isAccomBundle) {
    const bundledRates = propertyRates.filter(rate => rate.bundle?.bundleId === item.isAccomBundle)
    const bundledRateById = bundledRates.find(rate => rate.service.rateId === item.roomRateId || rate.rateId === item.roomRateId)
    if (bundledRateById) return bundledRateById
    if (bundledRates.length === 1) return bundledRates[0]
  }

  return propertyRates.find(rate => rate.service.rateId === item.roomRateId || rate.rateId === item.roomRateId)
}

function dummyBedbankItemView(
  intl: IntlShape,
  item: App.Checkout.BedbankHotelItem,
  kind: 'bedbank',
  offer?: App.BedbankOffer,
): App.Checkout.BedbankAccommodationItemView {
  return {
    kind,
    packageId: getPackageId(item),
    uniqueKey: getItemUniqueKey(item),
    item,
    image: {},
    primaryTitle: '',
    occupancy: { adults: 0, children: 0, infants: 0 },
    removeLabel: intl.formatMessage({
      id: 'kKF0I_jMAhUr5_wOULZ8',
      defaultMessage: 'Remove',
      description: 'Remove button label for bedbank accommodation item in checkout',
    }),
    soldOut: true,
    loyalty: {
      productType: getLoyaltyProductType(offer),
    },
    title: '',
    totals: {
      price: 0,
      memberPrice: 0,
      value: 0,
      surcharge: 0,
      taxesAndFees: 0,
      memberValue: 0,
      extraGuestSurcharge: 0,
    },
    propertyId: offer?.property.id,
  }
}

export function getBedbankItemView(
  intl: IntlShape,
  item: App.Checkout.BedbankHotelItem,
  // This is temporary until LPP member pricing is fully released
  luxPlusLPPEnabled: boolean,
  session: App.BedbankEnquiry | null,
  offer?: App.BedbankOffer,
  inputRate?: App.BedbankRate,
  propertyRatesByKey?: App.OfferState['propertyRates'],
  postPurchase?: App.CheckoutCartMode,
  existingOrder?: App.Order,
  ratesFetched?: boolean,
  lppMemberPricingEnabledOnOrder?: boolean,
  markupSource?: ChannelMarkupSource,
): App.WithDataStatus<App.Checkout.BedbankAccommodationItemView> {
  if (!offer) {
    return {
      hasRequiredData: false,
      data: dummyBedbankItemView(intl, item, 'bedbank', offer),
    }
  }

  // no rate means no availability
  if (!inputRate) {
    return {
      hasRequiredData: isPostPurchaseAncillaryCartMode(postPurchase) || (postPurchase === 'change-dates' || postPurchase === 'change-package') ? !!ratesFetched : false,
      data: dummyBedbankItemView(intl, item, 'bedbank', offer),
    }
  }

  // Shallow-clone so the in-place overrides below (session pricing, cancellation policies,
  // payment schedule) don't mutate the App.BedbankRate object held in Redux state.
  const rate = { ...inputRate }

  if (session?.occupancyPricing && session?.totals) {
    // Same pricing inputs as bedbankRateMap, so price-gated v2 campaigns resolve as on the offer page.
    const markupPricing = { total: session.totals.exclusive + session.totals.taxesAndFees, luxPlusPrice: session.totals.memberInclusive || undefined }
    const markupDetails = resolveChannelMarkupDetails(offer.id, OFFER_TYPE_BED_BANK, markupSource, markupPricing)
    const memberMarkupDetails = resolveMemberChannelMarkupDetails(offer.id, OFFER_TYPE_BED_BANK, markupSource, markupPricing)
    const markedSession = applyChannelMarkupToSessionPricing(session, markupDetails, memberMarkupDetails)
    rate.occupancyPricing = markedSession.occupancyPricing!
    rate.totals = markedSession.totals!
  }

  if (session?.cancellationPolicies && rate.refundable) {
    const oldDeadline = min(rate.cancellationPolicies.map(cp => new Date(cp.start)))
    const newDeadline = min(session.cancellationPolicies.map(cp => new Date(cp.start)))
    if (oldDeadline && newDeadline && !rate.cancellationPolicyChanged) {
      // if new deadline is at least 1 hr earlier
      rate.cancellationPolicyChanged = newDeadline < addHours(oldDeadline, -1)
    }

    rate.cancellationPolicies = session.cancellationPolicies
  }

  if (session) {
    // should always override including undefine
    rate.paymentScheduleInfo = session.paymentScheduleInfo
  }

  const pkg = rate.room

  const hasPromo = offer.promotions.length > 0 && item.duration >= offer.promotions[0].los

  const todayWithinOfferPeriod = hasPromo && dateIsAfter(new Date(), new Date(offer.promotions[0].periodSchedule.from)) &&
    dateIsBefore(new Date(), new Date(offer.promotions[0].periodSchedule.to))

  const includePromoInclusions = (offer.type === OFFER_TYPE_BED_BANK) ?
    hasPromo && todayWithinOfferPeriod &&
    isBookingWithinDateRange(item.checkIn, item.checkOut, offer.promotions[0].travelPeriod.from, offer.promotions[0].travelPeriod.to) :
    true

  const unifiedRate = findUnifiedBedbankRateForCheckoutItem(item, offer, propertyRatesByKey)
  function getInclusions(): {
    inclusions: Array<App.PackageInclusion> | undefined,
    bundleInclusions: Array<App.PackageInclusion> | undefined,
    optInBundleInclusions: Array<App.PackageInclusion> | undefined,
  } {
    const promotionInclusions = includePromoInclusions
      ? offer?.promotions?.find(p => rate?.promotionId === p.id)?.inclusions
      : undefined
    const rateInclusions = rate?.inclusions
      ? rate.inclusions.map<App.PackageInclusion>(item => ({
          id: item.id,
          description: item.description,
          symbol: item.symbol,
        }))
      : undefined
    const unifiedInclusions = unifiedRate?.inclusions ?? []
    const unifiedBundleInclusions = unifiedRate?.bundleInclusions ?? []
    const legacyInclusions = promotionInclusions ?? rateInclusions

    // Only mandatory bundles are backed by a materialised bundle rate (found via propertyRateRef).
    // Opt-in parents are bundle-tagged but described by the plain rate, so they take the legacy
    // path below and carry their ticket inclusions stamped on the item instead.
    if ((item.isAccomBundle && !item.bundleOptin) || item.loyaltyPropertyRateUpgrade) {
      const inclusions = unifiedInclusions.filter(
        i => (i.description ?? '').toLowerCase().trim() !== 'room only',
      )
      const bundleInclusions = unifiedBundleInclusions.length ? unifiedBundleInclusions : undefined
      return { inclusions, bundleInclusions, optInBundleInclusions: undefined }
    }

    return {
      inclusions: legacyInclusions,
      bundleInclusions: undefined,
      optInBundleInclusions: item.bundleOptin ? getOptInBundleInclusionsFromRate(unifiedRate, item) : undefined,
    }
  }

  const itemTotals = getPropertyRateUpgradeSourceItemTotals(item) ?? getBedbankItemViewTotals(rate, item.occupancy, luxPlusLPPEnabled)

  const soldOut = !itemTotals.price

  if ((postPurchase == 'change-dates' || postPurchase === 'change-package') && 'orderItemId' in item && existingOrder) {
    const orderItem = existingOrder.bedbankItems.find(orderItem => orderItem.id === item.orderItemId)

    if (orderItem) {
      const activeRooms = orderItem.rooms.filter(room => room.status === 'booked')
      const roomsLength = activeRooms.length
      // subtracting old room price from new room, each room is a checkout item so we need to divide sum by room.length
      const orderTotal = Math.round(sum(activeRooms.map(room => room.price)) * 100) / roomsLength

      itemTotals.paidPrice = sum(activeRooms.map(room => room.price))

      // Match the LuxPlus/LPP price used by the backend rebook.
      itemTotals.newPrice = lppMemberPricingEnabledOnOrder && itemTotals.memberPrice > 0
        ? itemTotals.memberPrice
        : itemTotals.price

      const priceDiff = Number(((Math.round(itemTotals.price * 100) - orderTotal) / 100).toFixed(2))
      const memberPriceDiff = luxPlusLPPEnabled ? Number(((Math.round(itemTotals.memberPrice * 100) - orderTotal) / 100).toFixed(2)) : 0

      itemTotals.price = priceDiff >= 0 ? priceDiff : 0
      itemTotals.memberPrice = memberPriceDiff >= 0 ? memberPriceDiff : 0

      // clear those prices that we don't want to display
      itemTotals.value = 0
      itemTotals.taxesAndFees = 0
      itemTotals.otherFees!.propertyFees = 0
    }
  }

  const inclusions = getInclusions()

  let appliedPropertyRateUpgrade: App.Checkout.AccommodationItemViewLoyaltyAppliedUpgrade | undefined
  if (isPropertyRateLoyaltyPackageUpgradeItem(item)) {
    const targetTotals = getBedbankItemViewTotals(rate, item.occupancy, luxPlusLPPEnabled)
    appliedPropertyRateUpgrade = {
      upgradeType: 'package',
      originTotals: itemTotals,
      originPrimaryTitle: item.loyaltyPropertyRateUpgrade?.sourceName ?? '',
      targetTotals,
      targetPrimaryTitle: unifiedRate?.name ?? pkg?.name ?? '',
      targetImage: pkg?.images[0] ?? offer.image,
      targetValue: targetTotals.value,
      targetInclusions: inclusions.inclusions,
    }
  }

  const data: App.Checkout.BedbankAccommodationItemView = {
    title: `${pkg?.name}: ${offer.name}`,
    kind: 'bedbank',
    packageId: item.roomId,
    uniqueKey: item.roomId,
    item,
    primaryTitle: pkg?.name ?? '',
    image: pkg?.images[0] ?? offer.image,
    occupancy: item.occupancy,
    rate,
    removeLabel: intl.formatMessage({
      id: 'YwsHiYKEsfSqYy2zz6Yw',
      defaultMessage: 'Remove room',
      description: 'Remove button label for bedbank room item in checkout',
    }),
    totals: itemTotals,
    ...inclusions,
    appliedPropertyRateUpgrade,
    soldOut,
    hasMobilePromotion: rate.mobilePromotion,
    loyalty: {
      productType: getLoyaltyProductType(offer),
      groupKey: item.sessionId,
    },
    mx: rate.mx,
    propertyId: offer.property.id,
  }

  const hasRequiredData = true

  if (!session) {
    return {
      hasRequiredData: ['change-dates', 'change-package', 'payment-schedule-balance'].includes(postPurchase ?? ''),
      data,
    }
  }

  return {
    hasRequiredData,
    data,
  }
}

export function getBedbankItemViewTotals(rate: App.BedbankRate, occupancy: App.Occupants, luxPlusLPPEnabled: boolean) {
  const pricing = rate.occupancyPricing.find(pricing =>
    pricing.occupancy === generateOccupancyStringByRoom(occupancy),
  ) || rate.occupancyPricing[0]

  // Channel markup (LPP) uplifts the tax-INCLUSIVE sell price (svc-order charges the same),
  // so the checkout price must mirror the marked `inclusive` — not `exclusive + un-marked tax`,
  // which drops markup on the tax portion and under-shows the total by ~tax×markup%.
  const memberInclusive = pricing.memberInclusive ?? 0
  const price = floatify(pricing.inclusive)
  const memberPrice = memberInclusive > 0 ? floatify(memberInclusive) : 0

  const itemTotals: App.Checkout.ItemViewTotals = {
    price,
    memberPrice: luxPlusLPPEnabled ? memberPrice : 0,
    value: pricing.inclusive,
    surcharge: 0,
    taxesAndFees: pricing.taxesAndFees ?? 0,
    otherFees: {
      propertyFees: pricing.propertyFees ?? 0,
    },
    paidPrice: 0,
    newPrice: 0,
    newSurcharge: 0,
    newExtraGuestSurcharge: 0,
    memberValue: 0,
    extraGuestSurcharge: 0,
  }

  return itemTotals
}
